/**
 * The service catalogue, agent-facing — Step 30, browse relaid to the Global2
 * capture's anatomy (Kai: "I much prefer this layout... favourites to the
 * top, and things sorted by groups / folders"): a left rail of category
 * folders under Favourites / Popular Items / All Service Items, a card grid
 * on the right, and a star on every card. Favourites are a DB-backed per-user
 * pin (they follow you to the portal); Popular is computed from real
 * submissions in the last 30 days, never curated. Rail choice, search and the
 * opened item all live in the URL — every view has a URL.
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Flame, LayoutGrid, Search, Send, Star } from 'lucide-react';
import { catalogueApi } from '../features/fields/api';
import { FieldRenderer } from '../features/fields/FieldRenderer';
import { CatalogueIcon } from '../features/fields/catalogueIcons';
import { RequesterPicker, type RequesterValue } from '../features/tickets/RequesterPicker';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Skeleton } from '../components/ui/skeleton';
import { qk } from '../lib/queryClient';
import { useDirty } from '../lib/dirty';
import { FileFieldInput } from '../features/fields/FileFieldInput';
import { attachmentError, type FileFieldValue } from '@itsm/shared';
import { useAuth } from '../lib/AuthContext';
import { useUrlState } from '../lib/urlState';
import { cn } from '../lib/utils';

export function CataloguePage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: categories } = useQuery({
    queryKey: qk.catalogueCategories,
    queryFn: catalogueApi.categories,
  });
  const { data, isPending, isError, error, refetch } = useQuery({
    queryKey: qk.catalogueItems.list('active'),
    queryFn: () => catalogueApi.items(true),
  });
  // Popular is the DEFAULT shelf (Kai) — falling back to All when nothing
  // is hand-picked yet, so a fresh org never lands on an empty page.
  const [railRaw, setRail] = useUrlState('cat', 'popular');
  const [search, setSearch] = useUrlState('q', '');
  const [selectedItemId, setSelectedItemId] = useUrlState('item', null);
  /**
   * Kai, 2026-09-08: *"the requester should always auto fill as yourself in
   * catalog"*. You are raising it, so it is yours until you say otherwise —
   * and with Step 72's "requested for" control beside it, moving it to
   * somebody else is now an explicit act rather than the starting state.
   * Still editable: an agent logging a request a customer phoned in sets the
   * requester to them.
   */
  const { me } = useAuth();
  const [requester, setRequester] = useState<RequesterValue>({
    email: me?.user.email ?? '',
    name: me?.user.name ?? '',
    isNew: false,
  });
  /**
   * Seed once `me` arrives.
   *
   * The initialiser above runs at mount, which is usually BEFORE the auth
   * probe resolves — so without this the field would sit empty for the one
   * case that matters (a hard refresh straight onto the catalogue) and be
   * prefilled only when navigating in from elsewhere. Guarded on still-empty
   * so it can never overwrite a requester somebody has already picked.
   */
  useEffect(() => {
    if (!me) return;
    setRequester((current) =>
      current.email ? current : { email: me.user.email, name: me.user.name ?? '', isNew: false },
    );
  }, [me]);
  const { data: form } = useQuery({
    queryKey: qk.catalogueItemForm(selectedItemId ?? '', requester.email || undefined),
    // Step 70 — keyed on the requester so switching the picker re-resolves
    // the prefill for the new person, which is the step's own check.
    queryFn: () => catalogueApi.itemForm(selectedItemId!, undefined, requester.email || undefined),
    enabled: !!selectedItemId,
  });

  const [subject, setSubject] = useState('');
  /** Step 73 — files chosen for this request, uploaded as `pending` and
   *  claimed onto the ticket on submit. */
  const [files, setFiles] = useState<FileFieldValue[]>([]);
  /** Step 77 — the OPTIONAL additional items ticked. Mandatory ones are not
   *  in here: they are included server-side whatever the client sends. */
  const [extras, setExtras] = useState<string[]>([]);
  /** Step 72 — who it is FOR, when that is not the person filling it in. */
  const [onBehalf, setOnBehalf] = useState(false);
  const [forPerson, setForPerson] = useState<RequesterValue>({ email: '', name: '', isNew: false });
  const [values, setValues] = useState<Record<string, unknown>>({});
  /**
   * Step 70 — seed the answers from the server-resolved prefill.
   *
   * Only fields the person has NOT already touched: re-applying on every
   * refetch would overwrite what they were typing, which is the one behaviour
   * worse than not prefilling at all.
   */
  useEffect(() => {
    const prefill = form?.prefill;
    if (!prefill) return;
    setValues((current) => {
      const next = { ...current };
      let changed = false;
      for (const [key, value] of Object.entries(prefill)) {
        const held = next[key];
        if (held === undefined || held === '') {
          next[key] = value;
          changed = true;
        }
      }
      return changed ? next : current;
    });
  }, [form?.prefill]);

  const [errors, setErrors] = useState<Record<string, string>>({});

  /**
   * A part-filled service request IS a draft — subject, requester and every
   * answer typed into the item's form. Navigating away without a prompt loses
   * all of it, which on a long form is the most expensive loss in the product.
   */
  const dirty =
    !!forPerson.email.trim() ||
    !!subject.trim() ||
    // Compared against the PREFILLED default, not against empty: the
    // requester now starts as you, so "is it non-empty" would be true on an
    // untouched form and prompt about unsaved work nobody did.
    requester.email !== (me?.user.email ?? '') ||
    files.length > 0 ||
    extras.length > 0 ||
    Object.values(values).some((v) => v !== undefined && v !== null && v !== '');
  const clearDirty = useDirty(dirty);

  const favourites = useMemo(() => new Set(data?.favourites ?? []), [data]);
  const star = useMutation({
    mutationFn: ({ id, on }: { id: string; on: boolean }) => catalogueApi.setFavourite(id, on),
    onSettled: () => queryClient.invalidateQueries({ queryKey: qk.catalogueItems.all }),
  });

  const submit = useMutation({
    mutationFn: () =>
      catalogueApi.submitRequest(selectedItemId!, {
        subject: subject.trim() || selectedItem?.name || 'Request',
        ...(files.length ? { attachmentIds: files.map((f) => f.id) } : {}),
        ...(extras.length ? { additionalItemIds: extras } : {}),
        requesterEmail: requester.email,
        ...(requester.name ? { requesterName: requester.name } : {}),
        // Step 72 — the ticket belongs to whoever it is FOR. Only sent when
        // the toggle is on and a person was chosen; `resolveParties` treats
        // choosing yourself as not-on-behalf anyway.
        ...(onBehalf && forPerson.email
          ? { onBehalfOf: { email: forPerson.email, ...(forPerson.name ? { name: forPerson.name } : {}) } }
          : {}),
        values,
      }),
    onSuccess: (res) => {
      // Retire the guard before navigating: the answers are still on screen
      // at this moment, so the unsaved-changes blocker would fire on the one
      // action that just saved every one of them.
      if (res.ok && res.body) {
        clearDirty();
        navigate(`/tickets/${res.body.id}`);
      } else setErrors((res.body as { details?: Record<string, string> } | null)?.details ?? {});
    },
  });

  const selectedItem = data?.entries.find((it) => it.id === selectedItemId);
  /**
   * Step 73 — the same rule the submit endpoint enforces, so the button is
   * disabled instead of the form being filled in and then refused. The server
   * is still the gate; this only stops the pointless round trip.
   */
  const fileProblem = selectedItem
    ? attachmentError(
        {
          allowAttachments: !!selectedItem.allowAttachments,
          requireAttachment: !!selectedItem.requireAttachment,
        },
        files.length,
      )
    : null;

  if (isPending) return <Skeleton className="m-6 h-64 w-full max-w-5xl" />;
  if (isError)
    return (
      <div className="m-6 max-w-4xl space-y-2 rounded-md border border-destructive/30 bg-destructive/10 p-3">
        <p className="text-sm font-medium text-destructive">Could not load the service catalogue</p>
        <p className="text-xs text-destructive/80">{(error as Error).message}</p>
        <button
          onClick={() => refetch()}
          className="text-xs text-destructive/80 underline hover:text-destructive"
        >
          Retry
        </button>
      </div>
    );

  if (!selectedItemId) {
    const rail = railRaw === 'popular' && (data?.popular ?? []).length === 0 ? 'all' : railRaw;
    const q = search.trim().toLowerCase();
    const matches = (data?.entries ?? []).filter(
      (it) => !q || it.name.toLowerCase().includes(q) || (it.description ?? '').toLowerCase().includes(q),
    );
    const roots = (categories?.entries ?? []).filter((c) => !c.parentId);
    const childIds = (rootId: string) => {
      // A rail folder shows its whole subtree (3-level cap enforced upstream).
      const ids = new Set([rootId]);
      let grew = true;
      while (grew) {
        grew = false;
        for (const c of categories?.entries ?? []) {
          if (c.parentId && ids.has(c.parentId) && !ids.has(c.id)) {
            ids.add(c.id);
            grew = true;
          }
        }
      }
      return ids;
    };
    const shown =
      rail === 'favourites'
        ? matches.filter((it) => favourites.has(it.id))
        : rail === 'popular'
          ? (data?.popular ?? [])
              .map((id) => matches.find((it) => it.id === id))
              .filter((it): it is NonNullable<typeof it> => !!it)
          : rail === 'all'
            ? matches
            : matches.filter((it) => (it.categoryIds ?? []).some((cid) => childIds(rail).has(cid)));

    const railEntries: { key: string; label: string; icon?: typeof Star }[] = [
      ...(favourites.size > 0 ? [{ key: 'favourites', label: 'Favourites', icon: Star }] : []),
      ...((data?.popular ?? []).length > 0 ? [{ key: 'popular', label: 'Popular items', icon: Flame }] : []),
      { key: 'all', label: 'All service items' },
      ...roots.map((c) => ({ key: c.id, label: c.name })),
    ];

    return (
      <div className="mx-auto max-w-6xl p-6">
        <div className="mb-4 flex flex-wrap items-center gap-3">
          <LayoutGrid className="h-5 w-5 text-muted-foreground" />
          <div>
            <h1 className="text-lg font-semibold">Service catalogue</h1>
            <p className="text-xs text-muted-foreground">Browse the services offered and raise a request</p>
          </div>
          <div className="relative ml-auto">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search services"
              className="h-9 w-64 pl-8"
            />
          </div>
        </div>
        <div className="flex flex-col gap-4 md:flex-row md:gap-6">
          {/* Step 141 follow-up — the category rail is a scrolling chip row on a
              phone and a vertical rail from `md:`. Side by side, the rail took
              208px of a 390px screen and the item grid was crushed into what
              was left. */}
          <nav className="-mx-1 flex gap-1 overflow-x-auto px-1 pb-1 md:mx-0 md:block md:w-52 md:shrink-0 md:space-y-0.5 md:overflow-visible md:px-0 md:pb-0">
            {railEntries.map((r) => (
              <button
                key={r.key}
                onClick={() => setRail(r.key)}
                className={cn(
                  'flex shrink-0 items-center gap-2 whitespace-nowrap rounded-md px-2.5 py-1.5 text-left text-sm transition-colors md:w-full md:whitespace-normal',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                  rail === r.key
                    ? 'bg-accent font-medium text-accent-foreground'
                    : 'text-muted-foreground hover:bg-accent/60 hover:text-foreground',
                )}
              >
                {r.icon && <r.icon className="h-4 w-4" />}
                <span className="truncate">{r.label}</span>
              </button>
            ))}
          </nav>
          <div className="min-w-0 flex-1">
            {rail === 'popular' && (
              <p className="mb-2 text-xs text-muted-foreground">Hand-picked by the support team</p>
            )}
            {shown.length === 0 ? (
              <p className="rounded-md border border-dashed p-6 text-center text-sm text-muted-foreground">
                {q ? `No services match “${search.trim()}”.` : 'Nothing here yet.'}
              </p>
            ) : (
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
                {shown.map((it) => (
                  <div
                    key={it.id}
                    className="group relative rounded-lg border bg-card p-3 transition-colors hover:border-brand hover:bg-brand/5"
                  >
                    <button
                      className="flex w-full items-start gap-2.5 text-left"
                      onClick={() => {
                        setSelectedItemId(it.id);
                        setSubject(it.name);
                      }}
                    >
                      <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-brand/10 text-brand">
                        <CatalogueIcon
                          name={it.icon}
                          attachmentId={it.iconAttachmentId}
                          className="h-4 w-4"
                        />
                      </span>
                      <span className="min-w-0 pr-6">
                        <span className="block truncate text-sm font-medium">{it.name}</span>
                        {it.description && (
                          <span className="mt-0.5 line-clamp-2 block text-xs text-muted-foreground">
                            {it.description}
                          </span>
                        )}
                      </span>
                    </button>
                    <button
                      aria-label={favourites.has(it.id) ? 'Remove from favourites' : 'Add to favourites'}
                      onClick={() => star.mutate({ id: it.id, on: !favourites.has(it.id) })}
                      className={cn(
                        'absolute right-2 top-2 rounded p-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                        favourites.has(it.id)
                          ? 'text-amber-500'
                          : 'text-muted-foreground/40 opacity-0 transition-opacity hover:text-muted-foreground group-hover:opacity-100 focus-visible:opacity-100',
                      )}
                    >
                      <Star className={cn('h-4 w-4', favourites.has(it.id) && 'fill-current')} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-2xl p-6">
      <button onClick={() => setSelectedItemId(null)} className="mb-3 text-xs text-brand hover:underline">
        ← Back to catalogue
      </button>
      <h1 className="mb-4 text-lg font-semibold">{selectedItem?.name}</h1>
      <div className="space-y-4">
        <div>
          <label className="mb-1 block text-sm font-medium">Requester</label>
          <RequesterPicker value={requester} onChange={setRequester} />
        </div>
        {/* Step 72 — requested-for, first class. Offered only where the item
            allows it: a person picker on a stationery request is a question
            nobody wants asked. The chosen person becomes the ticket's
            REQUESTER and whoever filled it in is recorded separately, so
            notifications, the portal and "my requests" all follow the right
            human. */}
        {/* `!!` is load-bearing, not noise. These are MySQL tinyints and the
            agent-side API sends them as 0/1 rather than booleans, so a bare
            `{item.allowOnBehalf && …}` renders a literal `0` on the form —
            which is exactly what it did (Kai, on the catalogue page). `0` is
            falsy, so the block correctly does not render; React then renders
            the `0` itself, because a number is valid JSX. */}
        {!!selectedItem?.allowOnBehalf && (
          <div className="rounded-md border p-3">
            <label className="flex items-start gap-2 text-sm">
              <input
                type="checkbox"
                checked={onBehalf}
                onChange={(e) => setOnBehalf(e.target.checked)}
                className="mt-0.5 h-4 w-4 rounded border-input"
              />
              <span>
                <span className="block font-medium">This is for someone else</span>
                <span className="block text-xs text-muted-foreground">
                  They become the requester; you are recorded as having raised it.
                </span>
              </span>
            </label>
            {onBehalf && (
              <div className="mt-2">
                <RequesterPicker value={forPerson} onChange={setForPerson} />
              </div>
            )}
          </div>
        )}
        {form?.subjectTemplated ? (
          <p className="rounded-md border border-dashed p-2.5 text-xs text-muted-foreground">
            The ticket subject is composed automatically from this item's subject template.
          </p>
        ) : (
          <div>
            <label className="mb-1 block text-sm font-medium">Subject</label>
            <Input value={subject} onChange={(e) => setSubject(e.target.value)} />
          </div>
        )}
        <FieldRenderer
          fields={form?.fields ?? []}
          values={values}
          errors={errors}
          onChange={(k, v) => setValues((prev) => ({ ...prev, [k]: v }))}
        />
        {/* Step 77 — the parts that come with this one. Mandatory items are
            STATED, never offered as a pre-ticked box somebody can clear:
            that is a default, not a requirement, and the two get confused. */}
        {!!form?.additionalItems?.length && (
          <div>
            <label className="mb-1 block text-sm font-medium">Comes with</label>
            <div className="space-y-1.5 rounded-md border p-2.5">
              {form.additionalItems.map((a) =>
                a.mandatory ? (
                  <p key={a.id} className="flex items-center gap-2 text-sm">
                    <CatalogueIcon name={a.icon} className="h-4 w-4 shrink-0 text-muted-foreground" />
                    {a.name}
                    <span className="text-xs text-muted-foreground">— always included</span>
                  </p>
                ) : (
                  <label key={a.id} className="flex cursor-pointer items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={extras.includes(a.id)}
                      onChange={(e) =>
                        setExtras((prev) =>
                          e.target.checked ? [...prev, a.id] : prev.filter((x) => x !== a.id),
                        )
                      }
                      className="h-4 w-4 accent-[hsl(var(--brand))]"
                    />
                    <CatalogueIcon name={a.icon} className="h-4 w-4 shrink-0 text-muted-foreground" />
                    {a.name}
                  </label>
                ),
              )}
            </div>
          </div>
        )}
        {/* Step 73 — the ITEM's own attachments, distinct from a `file`
            field. A file field is an answer to a question; this is "anything
            that helps", which is why it has no label of its own. */}
        {!!selectedItem?.allowAttachments && (
          <div>
            <label className="mb-1 block text-sm font-medium">
              Attachments
              {!!selectedItem.requireAttachment && <span className="ml-1 text-destructive">*</span>}
            </label>
            <FileFieldInput value={files} onChange={setFiles} maxFiles={10} maxSizeMb={15} />
            {fileProblem && <p className="mt-1 text-xs text-destructive">{fileProblem}</p>}
          </div>
        )}
        <Button
          disabled={
            (!form?.subjectTemplated && !subject.trim()) ||
            !requester.email ||
            !!fileProblem ||
            submit.isPending
          }
          onClick={() => submit.mutate()}
        >
          <Send /> Submit request
        </Button>
        {submit.data && !submit.data.ok && (
          <p className={cn('text-xs text-destructive')}>{submit.data.error}</p>
        )}
      </div>
    </div>
  );
}
